<script setup lang="ts">
import googlePlayStoreEnUrl from "shared-ui/assets/app-stores/get-it-on-google-play-badge-web-color-english.svg";
import googlePlayStoreSvUrl from "shared-ui/assets/app-stores/get-it-on-google-play-badge-web-color-swedish.svg";
import microsoftStoreEnUrl from "shared-ui/assets/app-stores/microsoft-store-en-us-light.svg";
import microsoftStoreSvUrl from "shared-ui/assets/app-stores/microsoft-store-sv-light.svg";
import wordpressMarketplaceEnUrl from "shared-ui/assets/app-stores/wordpress-marketplace-button-en.svg";
import wordpressMarketplaceSvUrl from "shared-ui/assets/app-stores/wordpress-marketplace-button-sv.svg";
import logoUrl from "shared-ui/assets/logo-white.svg";
import { useI18n } from "vue-i18n";

const { t, locale } = useI18n();

defineProps<{
  productLink: string;
  pricingLink: string;
  publicRiskAnalysisLink?: string;
  agentDownloadLink: string;
  resourceLink: string;
  partnersLink: string;
  companyLink: string;
  securityLink?: string;
  privacyPolicyLink: string;
  termsAndConditionsLink: string;
}>();
</script>

<template>
  <footer class="mt-20 flex w-full justify-center bg-primary-black/85">
    <div class="flex max-w-7xl flex-col gap-6 px-12 py-14">
      <div
        class="grid w-full grid-cols-1 justify-between gap-12 md:grid-cols-2"
      >
        <div class="mx-2 inline-flex flex-col items-start gap-9 sm:mx-0">
          <img
            :src="logoUrl"
            :alt="t('common.logo_alt')"
            class="h-auto w-38 text-white"
            width="136"
            height="34"
            loading="lazy"
          />
          <p class="text-lg text-secondary-white">
            {{ t("website_footer.slogan") }}
          </p>
          <div class="flex flex-col gap-4">
            <a
              href="https://play.google.com/store/apps/details?id=ai.controla.app"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                :src="
                  locale === 'sv' ? googlePlayStoreSvUrl : googlePlayStoreEnUrl
                "
                width="200"
                height="54"
                :alt="t('common.image_alts.google_play_store_download')"
                loading="lazy"
              />
            </a>
            <a
              href="https://wordpress.org/plugins/controla/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                :src="
                  locale === 'sv'
                    ? wordpressMarketplaceSvUrl
                    : wordpressMarketplaceEnUrl
                "
                width="200"
                height="53"
                :alt="t('common.image_alts.wordpress_marketplace_download')"
                loading="lazy"
              />
            </a>
            <a
              href="https://apps.microsoft.com/detail/xpfg2wr1d7xt87?referrer=appbadge&mode=full"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                :src="
                  locale === 'sv' ? microsoftStoreSvUrl : microsoftStoreEnUrl
                "
                width="200"
                height="54"
                :alt="t('common.image_alts.microsoft_store_download')"
                loading="lazy"
              />
            </a>
          </div>
        </div>

        <nav
          class="mx-2 inline-grid w-full grid-cols-1 gap-3 sm:grid-cols-2 md:mx-0 md:grid-cols-3"
        >
          <div class="inline-flex flex-col gap-3">
            <h4 class="text-base! font-normal! text-primary-white!">
              {{ t("website_footer.brand_heading") }}
            </h4>
            <a
              :href="productLink"
              class="text-secondary-white hover:text-stone-100"
            >
              {{ t("website_navigation.product") }}
            </a>
            <a
              :href="pricingLink"
              class="text-secondary-white hover:text-stone-100"
            >
              {{ t("website_navigation.pricing") }}
            </a>
            <a
              v-if="publicRiskAnalysisLink"
              :href="publicRiskAnalysisLink"
              class="text-secondary-white hover:text-stone-100"
            >
              {{ t("website_footer.cyber_security_check") }}
            </a>
            <a
              :href="agentDownloadLink"
              class="text-secondary-white hover:text-stone-100"
            >
              {{ t("website_footer.download_agent") }}
            </a>
            <a
              href="https://status.controla.ai/"
              class="text-secondary-white hover:text-stone-100"
            >
              {{ t("website_footer.service_status") }}
            </a>
          </div>
          <div class="inline-flex flex-col gap-3 pt-8 sm:pt-0">
            <h4 class="text-base! font-normal! text-primary-white!">
              {{ t("website_footer.about") }}
            </h4>
            <a
              :href="companyLink"
              class="text-secondary-white hover:text-stone-100"
            >
              {{ t("website_footer.company") }}
            </a>
            <a
              :href="resourceLink"
              class="text-secondary-white hover:text-stone-100"
            >
              {{ t("website_navigation.resources") }}
            </a>
            <a
              :href="partnersLink"
              class="text-secondary-white hover:text-stone-100"
            >
              {{ t("website_footer.partnerships") }}
            </a>
            <a
              v-if="securityLink"
              :href="securityLink"
              class="text-secondary-white hover:text-stone-100"
            >
              {{ t("website_navigation.security") }}
            </a>
            <a
              :href="privacyPolicyLink"
              class="text-secondary-white hover:text-stone-100"
            >
              {{ t("common.privacy_policy") }}
            </a>
            <a
              :href="termsAndConditionsLink"
              class="text-secondary-white hover:text-stone-100"
            >
              {{ t("common.terms_and_conditions") }}
            </a>
          </div>
          <div class="inline-flex flex-col gap-3 pt-8 md:pt-0">
            <h4 class="text-base! font-normal! text-primary-white!">
              {{ t("website_footer.socials") }}
            </h4>
            <a
              href="https://www.linkedin.com/company/mycontrola"
              class="text-secondary-white hover:text-stone-100"
            >
              {{ t("website_footer.linkedin") }}
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61574297188432"
              class="text-secondary-white hover:text-stone-100"
            >
              {{ t("website_footer.facebook") }}
            </a>
            <a
              href="https://www.youtube.com/@Controla-ai"
              class="text-secondary-white hover:text-stone-100"
            >
              {{ t("website_footer.youtube") }}
            </a>
          </div>
        </nav>
      </div>

      <Divider />

      <div
        class="flex flex-col gap-6 text-secondary-white md:flex-row md:justify-between"
      >
        <p>{{ t("website_footer.address") }}</p>
        <a class="text-secondary-white" href="tel:+468257375">{{
          t("website_footer.phone")
        }}</a>
        <p>{{ t("website_footer.copyright") }}</p>
      </div>
    </div>
  </footer>
</template>
